import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Radio, Lock } from "lucide-react";
import api from "@/lib/api";

const NC500_LAUNCH = new Date("2026-11-06T06:00:00Z");
const easeOut = [0.16, 1, 0.3, 1];

function useCountdown(target) {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    const diff = Math.max(0, target.getTime() - now.getTime());
    const s = Math.floor(diff / 1000);
    return {
        days: Math.floor(s / 86400),
        hours: Math.floor((s % 86400) / 3600),
        minutes: Math.floor((s % 3600) / 60),
        seconds: s % 60,
    };
}

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
}

export default function HomeTransmissionTeaser() {
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-15% 0px" });
    const countdown = useCountdown(NC500_LAUNCH);

    useEffect(() => {
        api.get("/pilot-log")
            .then((r) => setData(r.data))
            .catch(() => setData({ entries: [], next_unlock: null, signed_days_to_launch: null }))
            .finally(() => setLoaded(true));
    }, []);

    if (!loaded) return null;

    const latest = data?.entries?.[0];
    const nextUnlock = data?.next_unlock;

    // If there's nothing at all yet, still render — it becomes the "sealed" hero moment
    return (
        <section
            ref={ref}
            className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32"
            data-testid="home-transmission-teaser"
        >
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.9, ease: easeOut }}
                className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
            >
                {/* Left: meta + title */}
                <div className="lg:col-span-5">
                    <div className="flex items-center gap-3 mb-4 text-[var(--sb-brand)]">
                        <Radio size={16} />
                        <span className="text-xs tracking-[0.32em] uppercase">The Pilot&rsquo;s Log</span>
                    </div>
                    <h2
                        className="font-serif tracking-tight leading-[0.98] mb-6"
                        style={{ fontSize: "clamp(2.2rem, 5vw, 4.2rem)" }}
                    >
                        Transmissions from<br />
                        <span className="italic text-[var(--sb-brand)]">the cockpit.</span>
                    </h2>
                    <p className="text-[var(--sb-text-dim)] text-lg leading-relaxed mb-8 max-w-md">
                        A field journal that unlocks progressively as the NC500 draws closer. One transmission at a time — from prep to touchdown.
                    </p>

                    <div className="border-t border-[var(--sb-border)] pt-6">
                        <div className="overline mb-3">NC500 Launch · 6 Nov 2026</div>
                        <div className="flex items-end gap-6" data-testid="home-teaser-countdown">
                            {[
                                { label: "Days", value: countdown.days },
                                { label: "Hrs", value: countdown.hours },
                                { label: "Min", value: countdown.minutes },
                            ].map((u) => (
                                <div key={u.label} className="flex flex-col items-start">
                                    <span
                                        className="font-serif text-[var(--sb-text)] tabular-nums leading-none"
                                        style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)" }}
                                    >
                                        {String(u.value).padStart(2, "0")}
                                    </span>
                                    <span className="mt-1 text-[0.6rem] tracking-[0.32em] uppercase text-[var(--sb-text-muted)]">
                                        {u.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right: latest transmission OR sealed preview */}
                <div className="lg:col-span-7">
                    {latest ? (
                        <Link
                            to="/pilots-log"
                            className="group block border border-[var(--sb-border)] bg-[var(--sb-surface)] p-8 lg:p-10 transition-colors duration-500 hover:border-[var(--sb-brand)] relative overflow-hidden"
                            data-testid="home-teaser-latest"
                        >
                            <div className="absolute -top-10 -right-6 text-[10rem] font-serif italic leading-none text-[var(--sb-border)] group-hover:text-[var(--sb-brand)]/20 transition-colors duration-500">
                                01
                            </div>
                            <div className="relative">
                                <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.28em] uppercase text-[var(--sb-brand)]">
                                    {latest.day_label || "Latest transmission"}
                                    <span className="w-8 h-px bg-[var(--sb-brand)]" />
                                    <span className="text-[var(--sb-text-muted)]">{formatDate(latest.published_at)}</span>
                                </div>
                                <h3
                                    className="font-serif tracking-tight leading-[1.05] mb-5 group-hover:text-[var(--sb-brand)] transition-colors"
                                    style={{ fontSize: "clamp(1.6rem, 3vw, 2.6rem)" }}
                                >
                                    {latest.title}
                                </h3>
                                <p className="text-[var(--sb-text-dim)] leading-relaxed line-clamp-4 max-w-2xl">
                                    {latest.body}
                                </p>
                                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[var(--sb-brand)] mt-8 group-hover:gap-3 transition-all">
                                    Open the log <ArrowRight size={13} />
                                </span>
                            </div>
                        </Link>
                    ) : (
                        <div
                            className="relative border border-dashed border-[var(--sb-border)] bg-[var(--sb-surface)] p-10 lg:p-14 overflow-hidden"
                            data-testid="home-teaser-sealed"
                        >
                            <div
                                aria-hidden
                                className="absolute inset-0 opacity-[0.15]"
                                style={{
                                    background:
                                        "radial-gradient(60% 60% at 50% 40%, rgba(138,123,102,0.35) 0%, rgba(0,0,0,0) 60%)",
                                }}
                            />
                            <div className="relative">
                                <div className="flex items-center gap-3 mb-6 text-[var(--sb-brand)]">
                                    <Lock size={18} />
                                    <span className="text-xs tracking-[0.32em] uppercase">Sealed Transmission</span>
                                </div>
                                <h3
                                    className="font-serif tracking-tight leading-[1.05] mb-6"
                                    style={{ fontSize: "clamp(1.6rem, 3vw, 2.6rem)" }}
                                >
                                    The first entry is being written<br />
                                    <span className="italic text-[var(--sb-brand)]">as we speak.</span>
                                </h3>
                                <p className="text-[var(--sb-text-dim)] leading-relaxed mb-8 max-w-lg">
                                    Get pinged the moment it unlocks — one email, no newsletter, no noise.
                                </p>
                                <Link to="/pilots-log" className="btn-outline" data-testid="home-teaser-sealed-cta">
                                    Sign up for launch alerts <ArrowRight size={16} />
                                </Link>
                            </div>
                        </div>
                    )}

                    {nextUnlock && latest && (
                        <div className="mt-5 flex items-center gap-3 text-xs tracking-[0.28em] uppercase text-[var(--sb-text-muted)]" data-testid="home-teaser-next-unlock">
                            <Lock size={12} className="text-[var(--sb-brand)]" />
                            Next transmission {nextUnlock.day_label ? `· ${nextUnlock.day_label} ` : ""}unlocks {formatDate(nextUnlock.published_at)}
                        </div>
                    )}
                </div>
            </motion.div>
        </section>
    );
}
